<template>
    <div class="profile-view">
        <heading title="My Profile" tag="profile-view-title"/>
        <b-row>
            <b-col>
                <b-card-body>
                    <view-table
                        :model="userFields"
                        :data="userInfo"
                        :fields="displayFields"/>
                </b-card-body>
            </b-col>
        </b-row>
        <!-- company users -->
        <b-row v-if="companyUsers" class="mt-4">
            <b-col>
                <h3>
                    Associated Companies
                    <helper tag="profile-view-companies"/>
                </h3>
            </b-col>
        </b-row>
        <data-table ref="userCompanyTable"
                    :get-items="getCompanyUsers"
                    :fields="companyDisplayFields">
            <template v-slot:customField="slotProps">
                <div v-if="slotProps.row.field.key === 'name'">
                    <router-link :to="`/company/view/${slotProps.row.item.company_id}`">
                        {{ slotProps.row.item[slotProps.row.field.key] }}
                    </router-link>
                </div>
                <div v-else-if="slotProps.row.field.key === 'is_company_admin'">
                    {{ slotProps.row.item[slotProps.row.field.key] ? '&#10004;' : null }}
                </div>
                <div v-else-if="slotProps.row.field.key === 'actions'">
                    <b-button @click="editCompanyUser(slotProps.row.item)" size="sm" variant="primary" class="mr-2">
                        <b-icon-pencil/>
                    </b-button>
                    <b-button @click="deleteCompanyUser(slotProps.row.item)" size="sm" variant="danger">
                        <b-icon-trash/>
                    </b-button>
                </div>
            </template>
        </data-table>
        <!-- agency users -->
        <b-row v-if="agencyUsers">
            <b-col>
                <h3>
                    Associated Agencies
                    <helper tag="profile-view-agencies"/>
                </h3>
            </b-col>
        </b-row>
        <data-table ref="userAgencyTable"
                    :get-items="getAgencyUsers"
                    :fields="agencyDisplayFields">
            <template v-slot:customField="slotProps">
                <div v-if="slotProps.row.field.key === 'name'">
                    <router-link :to="`/agency/view/${slotProps.row.item.agency_id}`">
                        {{ slotProps.row.item[slotProps.row.field.key] }}
                    </router-link>
                </div>
                <div v-else-if="slotProps.row.field.key === 'is_program_admin'">
                    {{ slotProps.row.item[slotProps.row.field.key] ? '&#10004;' : null }}
                </div>
                <div v-else-if="slotProps.row.field.key === 'is_owners_representative'">
                    {{ slotProps.row.item[slotProps.row.field.key] ? '&#10004;' : null }}
                </div>
                <div v-else-if="slotProps.row.field.key === 'is_project_manager'">
                    {{ slotProps.row.item[slotProps.row.field.key] ? '&#10004;' : null }}
                </div>
                <div v-else-if="slotProps.row.field.key === 'actions'">
                    <b-button @click="editAgencyUser(slotProps.row.item)" size="sm" variant="primary" class="mr-2">
                        <b-icon-pencil-square/>
                    </b-button>
                    <b-button @click="deleteAgencyUser(slotProps.row.item)" size="sm" variant="danger">
                        <b-icon-trash/>
                    </b-button>
                </div>
            </template>
        </data-table>
        <b-row class="mx-1 my-4 mr-3">
            <b-button class="mr-3"
                      variant="primary"
                      :to="{name:'profileEdit'}">
                Edit My Profile
            </b-button>
            <b-button class="mr-3"
                      variant="primary"
                      :to="{name:'profilePassword'}">
                Change My Password
            </b-button>
            <b-button variant="danger"
                      @click="accountDelete()">
                Delete My Account
            </b-button>
        </b-row>
    </div>

</template>

<script>
import Vue from 'vue'
import UserFields from '@/models/User'

export default Vue.extend({
    name: 'ProfileView',
    data () {
        return {
            returnLabel: '',
            userInfo: {},
            agencyUsers: [],
            companyUsers: [],
            userFields: UserFields,
        }
    },
    computed: {
        displayFields () {
            return [
                'first_name',
                'last_name',
                'email',
                'phone_office',
                'phone_mobile',
                'is_superadmin',
                'is_admin',
                'is_auditor',
            ]
        },
        agencyDisplayFields () {
            return [
                'name',
                'is_program_admin',
                'is_owners_representative',
                'is_project_manager',
                'company_name',
                'department',
            ]
        },
        companyDisplayFields () {
            return [
                'name',
                'is_company_admin',
            ]
        },
    },
    methods: {
        getAgencyUsers () {
            return Promise.resolve(this.userInfo ? this.userInfo.agencyUsers : [])
        },
        getCompanyUsers () {
            return Promise.resolve(this.userInfo ? this.userInfo.companyUsers : [])
        },
        refresh () {
            this.$api.getItem(this, 'user', this.$user.userId())
                .then((data) => {
                    //  add agency name to agencyUsers list
                    if (data.agencyUsers && data.agencies) {
                        data.agencyUsers.forEach((agencyUser) => {
                            let agency_id = agencyUser.agency_id
                            data.agencies.forEach((agency) => {
                                if (agency.id === agency_id) {
                                    agencyUser.name = agency.name
                                }
                            })
                        })
                    }
                    //  add company name to companyUsers list
                    if (data.companyUsers && data.companies) {
                        data.companyUsers.forEach((companyUser) => {
                            let company_id = companyUser.company_id
                            data.companies.forEach((company) => {
                                if (company.id === company_id) {
                                    companyUser.name = company.name
                                }
                            })
                        })
                    }
                    this.userInfo = data
                    if (this.$refs.userAgencyTable) {
                        this.$refs.userAgencyTable.refresh()
                    }
                    if (this.$refs.userCompanyTable) {
                        this.$refs.userCompanyTable.refresh()
                    }
                })
        },
        accountDelete () {
            this.$bvModal.msgBoxConfirm('Would you like to delete your account? This action cannot be undone.')
                .then(async value => {
                    if (value) {
                        const userInfo = { ...this.userInfo, disabled: true }
                        console.log('ProfileView:accountDelete  userInfo=' + JSON.stringify(userInfo))
                        this.$api.updateItem(this, 'user', userInfo)
                            .then(() =>
                                // logout user
                                this.$user.logoutUser(Vue, this.$store.state.currentUser.id))
                            .then(() => {
                                // toast
                                Vue.toasted.info('Your account has been deleted')
                                // switch to home screen
                                this.$router.push('/')
                            })
                            .catch((error) => {
                                console.log(error)
                                const message = error.body.message || ' a server error occured'
                                Vue.toasted.error('Unable to delete your account because ' + message)
                            })
                    }
                })
        },
    },
    created () {
        this.refresh()
    },
})
</script>
